import {
  ChatContentResultTypenames,
  ChatEventType,
  ChatSender,
  CustomerChatMessage,
  SendCustomerChatMessageResult,
  SendCustomerChatMessageResultTypenames,
} from '@varo/graphql-schema-types';
import {
  CHAT_START_MSG,
  getHistory,
} from 'features/chat/helpers/ChatEvent.helper';
import { ChatContext } from 'features/chat/providers/ChatContextProvider';
import { useContext, useEffect, useState } from 'react';
import SendMessage from 'services/graphql/mutations/Chat/SendMessage';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useCancellableMutation from 'shared/hooks/useCancellableMutation';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { logError } from 'shared/utils/sentry/sentry';

export const useSendMessage = (
  setShowSnackbar?: (showSnackbar: boolean) => void,
) => {
  const {
    isChatStarted,
    startBotChat,
    setChatSessionId,
    setHistory,
    setIsChatEndedFromEvent,
    setIsAgentTyping,
  } = useContext(ChatContext);

  const [isChatStartMsg, setIsChatStartMsg] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');

  const [{ fetching, data }, sendCustomerChatMessage] = useCancellableMutation<{
    sendCustomerChatMessage: SendCustomerChatMessageResult;
  }>(SendMessage);

  const handleSessionStartedAnalytics = usePressableAnalytics({
    analyticsId: 'chatSessionStarted',
    customAttributes: {
      chat_session_id: sessionId,
    },
  });

  useEffect(() => {
    if (sessionId && isChatStartMsg) {
      handleSessionStartedAnalytics();
    }
  }, [sessionId, isChatStartMsg, handleSessionStartedAnalytics]);

  const sendCustomerMessage = async (
    message: string,
    sender: ChatSender,
    isDecagon: boolean,
  ) => {
    const isChatInitialization = message === CHAT_START_MSG;

    try {
      const response = await sendCustomerChatMessage({
        idempotencyId: getUuid(),
        input: {
          message,
          sender,
          isDecagon,
        },
      });
      if (
        response.data?.sendCustomerChatMessage.__typename ===
        SendCustomerChatMessageResultTypenames.SendCustomerChatMessageSuccess
      ) {
        const sendMessageResponse: CustomerChatMessage =
          response.data.sendCustomerChatMessage.chatMessage;

        const newSessionId = sendMessageResponse.sessionId ?? '';
        if (newSessionId && newSessionId !== sessionId) {
          setSessionId(newSessionId);
        }

        if (!isChatStarted) {
          if (newSessionId) {
            setChatSessionId(newSessionId);
          }
          startBotChat(newSessionId);
          setIsChatEndedFromEvent(false);
          // Keep typing indicator on until the first chat message arrives.
          // It will be cleared by the Message event handler.
          setIsAgentTyping(true);
        }

        setIsChatStartMsg(isChatInitialization);

        if (isChatInitialization) {
          const messageEvent: CustomerChatMessage = {
            id: sendMessageResponse.id,
            sender: ChatSender.UndefinedChatSender,
            createdAt: sendMessageResponse.createdAt,
            eventType: ChatEventType.UndefinedEventType,
            content: {
              message,
              __typename: ChatContentResultTypenames.ChatContentMessage,
            },
            sessionId: sendMessageResponse.sessionId,
          };

          setHistory((history) => getHistory(history, messageEvent));
        }
      }

      if (
        response?.data?.sendCustomerChatMessage?.__typename ===
        SendCustomerChatMessageResultTypenames.SendCustomerChatMessageError
      ) {
        setShowSnackbar?.(true);
        logError(
          `Error ${response.data.sendCustomerChatMessage.title} : ${response.data.sendCustomerChatMessage.message}`,
        );
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        return;
      }
      setShowSnackbar?.(true);
      logError(
        `Error sending chat message: ${(err as Error)?.message ?? 'unknown'}`,
      );
    }
  };

  return {
    sendCustomerMessage,
    sendMessageFetching: fetching,
    sendMessageResponse: data,
  };
};
